import { useEffect, useRef, useState } from 'react'
import { api, type ConferenceTimeQuery, type ConferenceTimeReport } from './api.ts'

/** How often to ask again while the ERP is being read. */
const POLL_MS = 3_000

/**
 * One period's report, asked again while the bench half is still being read.
 *
 * The server reads the ERP in the background and answers `running` until it is
 * done; this keeps asking at a gentle pace and stops the moment it is not.
 * `nonce` is how the screen forces a fresh read (the "ler de novo" button):
 * changing it sends `refresh` once.
 */
export function useConferenceTime(
  query: Omit<ConferenceTimeQuery, 'refresh'> | undefined,
  nonce: number,
): { report?: ConferenceTimeReport | undefined; error?: string | undefined } {
  const [report, setReport] = useState<ConferenceTimeReport | undefined>()
  const [error, setError] = useState<string | undefined>()
  const key = query === undefined ? '' : JSON.stringify(query)
  // The nonce last sent as a refresh, so changing the hours afterwards does
  // not re-read the ERP: only pressing the button again does.
  const refreshed = useRef(0)

  useEffect(() => {
    if (query === undefined) return undefined
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    setReport(undefined)
    setError(undefined)

    const ask = (refresh: boolean) => {
      api
        .conferenceTime({ ...query, refresh })
        .then((next) => {
          if (cancelled) return
          setReport(next)
          if (next.bench.state === 'running') timer = setTimeout(() => ask(false), POLL_MS)
        })
        .catch((cause: unknown) => {
          if (!cancelled) setError(cause instanceof Error ? cause.message : 'falhou')
        })
    }
    const refresh = nonce > refreshed.current
    refreshed.current = nonce
    ask(refresh)

    return () => {
      cancelled = true
      if (timer !== undefined) clearTimeout(timer)
    }
    // `key` stands for `query`, which is a fresh object on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, nonce])

  return { report, error }
}
